import Phaser from 'phaser';
import {
  type GameSetup,
  DEFAULT_SETUP,
  seatsToInputs,
} from '../../game/setup.js';
import {
  computeRegionBonus,
  createInitialState,
} from '../../game/state.js';
import { sameTeam } from '../../game/validation.js';
import type {
  AttackSubPhase,
  CombatResult,
  GameEvent,
  GameState,
  PlayerId,
  TerritoryId,
  TerritoryState,
} from '../../game/types.js';
import { DEFAULT_MAP, MAPS } from '../../maps/index.js';
import type { MapDefinition } from '../../maps/schema.js';
import { GameStore } from '../../store/GameStore.js';
import { RemoteGameStore } from '../../store/RemoteGameStore.js';
import type { Store } from '../../store/Store.js';
import { AiDriver } from '../AiDriver.js';
import {
  ADJACENCY_LINE_WIDTH,
  BUTTON_HEIGHT,
  COLORS,
  FONT_BODY,
  FONT_HEADER,
  FONT_MONO,
  TEAM_COLORS_INT,
  teamColorHex,
} from '../style.js';
import { Button } from '../view/Button.js';
import { CombatVfx } from '../view/CombatVfx.js';
import { ConfirmModal } from '../view/ConfirmModal.js';
import { ConnectionStatusChip } from '../view/ConnectionStatusChip.js';
import { DiceTray } from '../view/DiceTray.js';
import { AutoSkipToast } from '../view/AutoSkipToast.js';
import { EliminationToast } from '../view/EliminationToast.js';
import { BattleLogModal } from '../view/BattleLogModal.js';
import { GameOverModal, type PodiumEntry } from '../view/GameOverModal.js';
import { mountPageHeader } from '../view/PageHeader.js';
import { RegionOverlay } from '../view/RegionOverlay.js';
import { formatTurnTimer } from '../view/turnTimerFormat.js';
import { attachBottomNav, preloadNavAssets } from '../view/AppNav.js';
import { Stepper } from '../view/Stepper.js';
import { TerritoryView } from '../view/TerritoryView.js';
import { navigateBack } from '../sceneNav.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH, LAYOUT, MAP_Y_OFFSET } from '../PhaserGame.js';

// Team palette: deep, earthy non-pastel tones that fit the modern
// tactical / terminal look on the dark navy canvas. Three are active
// (Maroon / Navy / Forest); two more are reserved for additional seats
// (Autumn Orange #b8581f, Dark Gray #4a5260). All five clear WCAG AA
// for the white unit-count text on top.
// Default seat configuration is now provided by `DEFAULT_SETUP` (in
// src/game/setup.ts). The lobby's GameSetup carries the seats list
// straight through scene data into createInitialState below.

// Per-game seed derived at scene start so each play (or "Play Again"
// restart) gets a fresh territory distribution + RNG stream. Stash on
// the scene rather than module scope so the value is logged in DevTools
// state if we ever need to repro a layout.
function freshSeed(): number {
  return Date.now() ^ Math.floor(Math.random() * 0x7fffffff);
}

export class GameScene extends Phaser.Scene {
  private store!: Store;
  // Pre-built remote store passed in by JoinScene; if set, create()
  // uses it directly instead of constructing a local GameStore.
  private incomingRemoteStore: RemoteGameStore | null = null;
  private readonly territoryViews = new Map<TerritoryId, TerritoryView>();

  // Map + seat configuration chosen in the lobby, passed in via scene
  // data. Both fall back to defaults — keeps direct scene.start('Game')
  // calls working (e.g. tests, deep links). Phaser's scene.restart()
  // reuses init data, so Play Again preserves the lobby choices.
  private chosenMap: MapDefinition = DEFAULT_MAP;
  private setup: GameSetup = DEFAULT_SETUP;

  // Selection state — `selected` is the action source, `target` is the
  // attack/fortify destination. Cleared on phase or player transition.
  private selectedTerritory: TerritoryId | null = null;
  private targetTerritory: TerritoryId | null = null;
  private attackingDice: 1 | 2 | 3 = 1;
  private advanceCount = 1;
  private fortifyCount = 1;

  // Held while the dice roll plays, so territory unit counts and the
  // action panel don't update underneath the animation. Also stays
  // true throughout an event-queue drain so the player can't tap
  // anything while an AI chain is animating.
  private combatAnimating = false;

  // --- Event queue (drains a batch of events one at a time so each
  // AI combat plays its own dice animation instead of all but the
  // first snapping to final state). MP server-side AI driver sends
  // many events in one Realtime update; we walk them with proper
  // pacing here.
  private eventQueue: GameEvent[] = [];
  private isDraining = false;
  // Player whose action the UI is currently animating. Set from the
  // first event's actor at drain start, then updated per-event as
  // the queue walks through events with different actors (e.g., AI 1
  // → AI 2 in a 2v2-ai chain). refreshStatus + refreshActionPanel
  // read this when set so the team chip + action panel name the
  // player whose events are actually playing — rather than the
  // store's final-state currentPlayer, which by drain time has
  // already advanced past everyone in the batch. Null outside of
  // an active drain; refreshAll then falls back to the store state.
  private displayedPlayer: PlayerId | null = null;
  // Walking snapshot of territory state at the current playback
  // position. Reset to store state at the end of every drain. We
  // mutate this forward as each event renders so a combat halfway
  // through a batch animates against the unit counts that existed
  // at THAT step, not the final post-batch state.
  private viewTerritories: Record<TerritoryId, TerritoryState> = {};
  // Game-over modal is deferred to fire after the drain finishes so
  // it lands cleanly at the END of any chained AI moves rather than
  // mid-batch. The elimination TOAST, by contrast, fires inline on
  // the playerEliminated event so the eliminated player gets
  // immediate feedback the moment their last tile flips — the toast
  // sits in the top-of-map HUD area and doesn't overlap the action
  // panel where subsequent dice rolls play out.
  private pendingGameWon = false;

  // Tracks last-seen phase/player so we know when to clear selection.
  private lastPhase: GameState['turn']['phase'] | undefined = undefined;
  private lastPlayer: PlayerId | undefined = undefined;
  // Tracks the destination tile of the most recent awaitingAdvance
  // sub-phase. Lets refreshAdvancePanel detect a NEW conquest (vs.
  // an in-flight one we're re-rendering) so it can default to
  // maxUnits while still preserving any manual stepper adjustment
  // the user made afterward.
  private lastAdvanceTo: TerritoryId | undefined = undefined;

  // Action panel header: a colored team chip (with "NAME: TURN N") on
  // the left, a color-coded phase badge in the middle, and a digital
  // countdown timer on the right. Then title / detail / stepper-or-dice
  // / buttons below.
  private teamChipBg!: Phaser.GameObjects.Rectangle;
  private teamChipText!: Phaser.GameObjects.Text;
  // Team summary strip — one Text object per team in play, sitting in
  // the empty band between the HUD and the first row of map tiles.
  // Each team's segment renders in its own team-palette color so the
  // T1↔cyan / T2↔amber / etc. mapping becomes visual. Hidden in pure
  // FFA games (every seat on its own team).
  private teamSummaryTexts: Phaser.GameObjects.Text[] = [];
  private phaseChipBg!: Phaser.GameObjects.Rectangle;
  private phaseChipText!: Phaser.GameObjects.Text;
  private timerChipBg!: Phaser.GameObjects.Rectangle;
  private timerText!: Phaser.GameObjects.Text;
  // Multiplayer-only "YOU · NAME" chip. Sits in the same slot as the
  // timer chip (top-right of the action panel) and stays visible the
  // whole game so the player can tell at a glance which seat this
  // browser controls — distinct from the team chip which shows the
  // CURRENT player. Hidden entirely in single-player.
  private youChipBg!: Phaser.GameObjects.Rectangle;
  private youChipText!: Phaser.GameObjects.Text;
  // Tween that pulses the team chip border in MP when it's the local
  // player's turn. Created on demand, cleaned up on shutdown.
  private teamChipPulse: Phaser.Tweens.Tween | null = null;
  private actionTitleText!: Phaser.GameObjects.Text;
  private actionDetailText!: Phaser.GameObjects.Text;

  // Turn-clock state. The clock counts down from TURN_DURATION_SEC and
  // resets whenever the phase or active player changes. No game effect
  // when it hits 0:00 yet — TBD what should happen then.
  private static readonly TURN_DURATION_SEC = 60;
  private turnTimerStartMs = 0;
  private lastDisplayedSec = -1;
  private stepper!: Stepper;
  private actionButton!: Button; // contextual action: Deploy 1 / Roll / Advance N
  private endPhaseButton!: Button; // End Deploy / End Attack / End Turn / End Setup Deploy
  // Compact secondary button visible only during deploy phase when
  // there's at least one deploy on this turn's stack to reverse.
  // Sits above the main button row.
  private undoButton!: Button;

  // Combat overlay
  private diceTray!: DiceTray;
  private combatVfx!: CombatVfx;

  // End-of-round overlays. Toast announces an elimination at the top of
  // the map; modal opens after game-over. The modal is created lazily
  // because we may never reach victory in a session.
  private eliminationToast!: EliminationToast;
  private autoSkipToast!: AutoSkipToast;
  private gameOverModal: GameOverModal | undefined;
  private battleLogModal: BattleLogModal | undefined;

  // Colored ring around each region that's fully controlled by one
  // player — visual surfacing of the territory-bonus mechanic.
  private regionOverlay!: RegionOverlay;

  private unsubscribe?: () => void;
  private aiDriver?: AiDriver;
  // MP-only — null in single-player. Surfaces Supabase Realtime
  // health in the top-right of the HUD.
  private connectionChip: ConnectionStatusChip | null = null;
  private unsubscribeConnection?: () => void;

  constructor() {
    super('Game');
  }

  preload(): void {
    preloadNavAssets(this);
    // Leading slash so Phaser resolves against site root regardless of
    // current URL path — `/game/:id` deep links would otherwise miss it.
    this.load.image('logo', '/assets/logo2.png');
  }

  init(data?: {
    mapId?: string;
    setup?: GameSetup;
    remoteStore?: RemoteGameStore;
  }): void {
    // Multiplayer: JoinScene hands us a fully-wired RemoteGameStore.
    // We use it as-is; map + state come from the remote snapshot
    // baked into the store, no local createInitialState call needed.
    if (data?.remoteStore) {
      this.incomingRemoteStore = data.remoteStore;
      this.chosenMap = data.remoteStore.getMap();
      this.setup = DEFAULT_SETUP; // unused in MP path but field is required
      return;
    }
    this.incomingRemoteStore = null;

    // The lobby passes a full GameSetup. Older callers (the temporary
    // home-screen map picker, deep links) may pass just `mapId`; they
    // get the default seat configuration. With nothing, full defaults.
    const setup = data?.setup;
    if (setup) {
      this.setup = setup;
      const m = MAPS.find((x) => x.id === setup.mapId);
      this.chosenMap = m ?? DEFAULT_MAP;
      return;
    }
    const requested = data?.mapId;
    this.chosenMap = requested
      ? MAPS.find((m) => m.id === requested) ?? DEFAULT_MAP
      : DEFAULT_MAP;
    this.setup = DEFAULT_SETUP;
  }

  create(): void {
    // scene.restart() reuses the JS instance — class field defaults do
    // NOT re-run. Reset per-game scene state explicitly so "Play Again"
    // (and game-switching from the inbox) doesn't carry selections,
    // animation locks, queued events, or stale view refs from the
    // previous session.
    //
    // isDraining + eventQueue + displayedPlayer in particular MUST be
    // reset here. If a user navigated away from Game A while
    // drainEventQueue was mid-await (AI mid-turn), the SHUTDOWN
    // unsubscribed the store but the `while` loop in drainEventQueue
    // kept its closure alive — isDraining stayed true across the
    // re-create, and handleTerritoryTap's first gate silently
    // swallowed every tap on Game B until reload. Don't drop these.
    this.selectedTerritory = null;
    this.targetTerritory = null;
    this.attackingDice = 1;
    this.advanceCount = 1;
    this.fortifyCount = 1;
    this.combatAnimating = false;
    this.isDraining = false;
    this.eventQueue = [];
    this.displayedPlayer = null;
    this.lastPhase = undefined;
    this.lastPlayer = undefined;
    this.lastAdvanceTo = undefined;
    this.territoryViews.clear();
    this.gameOverModal = undefined;
    this.battleLogModal = undefined;
    this.teamChipPulse = null;

    // Multiplayer path: reuse the pre-built remote store from JoinScene.
    // Single-player path: spin up a fresh local store with the lobby's
    // chosen setup.
    if (this.incomingRemoteStore) {
      this.store = this.incomingRemoteStore;
    } else {
      this.store = new GameStore(
        createInitialState({
          map: this.chosenMap,
          players: seatsToInputs(this.setup.seats),
          seed: freshSeed(),
        }),
        this.chosenMap
      );
    }

    this.drawZones();
    this.drawHexGrid();
    this.drawHud();
    this.drawAdjacencies();
    // Region-control overlay sits at its own depth (6), so creation
    // order with drawTerritories doesn't visually matter — but creating
    // it before drawTerritories keeps the conceptual layering "map
    // structure first, dynamic content second."
    // Use the actual chosen map — `DEFAULT_MAP` here would compute
    // edge geometry for the wrong region ids and tile size, leaving
    // every ring lookup empty on Slash and Terra.
    this.regionOverlay = new RegionOverlay(this, this.chosenMap);
    this.drawTerritories();
    this.drawActionPanel();
    this.drawDiceTray();
    this.drawNavBar();
    this.refreshAll();
    // Seed the playback snapshot so the first event batch (e.g., a
    // hot-seat AI's opening turn) animates against real starting
    // unit counts.
    this.viewTerritories = { ...this.store.getState().territories };

    this.resetTurnTimer();

    this.unsubscribe = this.store.subscribe((_state, events) =>
      this.handleStoreUpdate(events)
    );

    // AI driver: subscribes to the same store; ticks whenever an AI
    // seat is up. Started AFTER the scene's own subscription so the
    // visual update fires before AI's first tick is scheduled.
    //
    // Multiplayer (remote store) skips this — Phase 1 multiplayer is
    // all-human seats. Phase 3 will revisit when AI fallback for
    // missed-deadline turns lands.
    if (!this.incomingRemoteStore) {
      this.aiDriver = new AiDriver(this, this.store as GameStore);
      this.aiDriver.start();
    }

    // Connection-status chip is MP-only — LocalGameStore reports
    // 'connected' forever, so a chip there would be pure noise.
    if (this.store.isMultiplayer()) {
      this.connectionChip = new ConnectionStatusChip(
        this,
        this.store.getConnectionStatus()
      );
      this.unsubscribeConnection = this.store.onConnectionChange((status) => {
        this.connectionChip?.setStatus(status);
      });
    }

    // Closure-capture the store we just wired so SHUTDOWN destroys
    // THIS instance, not whatever `this.store` happens to point at
    // when SHUTDOWN fires. Defensive against any scene re-create
    // race where create() might run again before SHUTDOWN finishes —
    // destroying the wrong (current, live) store would leave a dead
    // realtime channel and a scene that can't dispatch.
    const storeForShutdown = this.store;
    const aiDriverForShutdown = this.aiDriver;

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      aiDriverForShutdown?.stop();
      this.teamChipPulse?.stop();
      this.unsubscribeConnection?.();
      this.connectionChip?.destroy();
      this.connectionChip = null;
      // Tear down the store BEFORE the scene actually unloads —
      // RemoteGameStore.destroy() removes its Supabase Realtime
      // channel so a re-entry from JoinScene can create a fresh
      // channel for the same gameId without colliding with the
      // already-subscribed instance still cached in the supabase
      // client's registry. Throws "cannot add postgres_changes
      // callbacks ... after subscribe()" otherwise.
      storeForShutdown?.destroy();
    });
  }

  override update(time: number): void {
    if (!this.timerText) return;

    // MP path: 24h turn countdown derived from
    // store.getTurnExpiresAt(). Gated on integer-second changes so
    // the per-second tick under 10m doesn't repaint every frame.
    // For the wider granularity (>10m) we still tick every second
    // here but refreshMpTimerChip's own gating keeps the displayed
    // text at minute granularity.
    if (this.store.isMultiplayer()) {
      const nowSec = Math.floor(Date.now() / 1000);
      if (nowSec === this.lastDisplayedSec) return;
      this.lastDisplayedSec = nowSec;
      this.refreshMpTimerChip();
      return;
    }

    // SP path: existing 60-sec MM:SS turn timer (unchanged).
    const elapsedSec = (time - this.turnTimerStartMs) / 1000;
    const remaining = Math.max(
      0,
      Math.ceil(GameScene.TURN_DURATION_SEC - elapsedSec)
    );
    if (remaining === this.lastDisplayedSec) return;
    this.lastDisplayedSec = remaining;
    const m = Math.floor(remaining / 60)
      .toString()
      .padStart(2, '0');
    const s = (remaining % 60).toString().padStart(2, '0');
    this.timerText.setText(`${m}:${s}`);
    if (remaining <= 10 && remaining > 0) {
      this.timerText.setColor(COLORS.accentRedHex);
      this.timerChipBg.setStrokeStyle(2, COLORS.accentRedInt);
    } else if (remaining === 0) {
      this.timerText.setColor(COLORS.accentRedHex);
      this.timerChipBg.setStrokeStyle(2, COLORS.accentRedInt);
    } else {
      this.timerText.setColor(COLORS.accentCyanHex);
      this.timerChipBg.setStrokeStyle(2, COLORS.accentCyanInt);
    }
  }

  private resetTurnTimer(): void {
    this.turnTimerStartMs = this.time.now;
    this.lastDisplayedSec = -1;
  }

  // --- Drawing ---

  private drawZones(): void {
    const g = this.add.graphics();

    // Top HUD strip
    g.fillStyle(COLORS.bgPanelInt, 1);
    g.fillRect(0, LAYOUT.topSafe, DESIGN_WIDTH, LAYOUT.hudHeight);

    // Layout zones from bottom up: nav, action, dice tray, map.
    const mapY = LAYOUT.topSafe + LAYOUT.hudHeight;
    const actionY =
      DESIGN_HEIGHT - LAYOUT.bottomSafe - LAYOUT.navHeight - LAYOUT.actionHeight;
    const trayY = actionY - LAYOUT.diceTrayHeight;

    // Map zone (canvas) — runs from below the HUD to the top of the tray.
    g.fillStyle(COLORS.bgDeepInt, 1);
    g.fillRect(0, mapY, DESIGN_WIDTH, trayY - mapY);

    // Dice tray — semi-transparent navy strip that sits between the map
    // and the action panel. Hosts the always-visible 3v3 dice display.
    g.fillStyle(COLORS.diceTrayBgInt, 0.85);
    g.fillRect(0, trayY, DESIGN_WIDTH, LAYOUT.diceTrayHeight);

    // Bottom action panel
    g.fillStyle(COLORS.bgPanelInt, 1);
    g.fillRect(0, actionY, DESIGN_WIDTH, LAYOUT.actionHeight);

    // Cyan accent lines separating panels. Drawn on their own Graphics
    // with a higher depth so the hex grid (drawn next) doesn't cross
    // them and break the strokes into apparent dashes. The two lines
    // that border the map zone get a 3px solid stroke; the tray/action
    // line below the dice stays 1px since no hex grid runs through it.
    const accents = this.add.graphics();
    accents.setDepth(5);
    accents.lineStyle(3, COLORS.accentCyanInt, 1);
    accents.lineBetween(0, mapY, DESIGN_WIDTH, mapY); // hud / map
    accents.lineBetween(0, trayY, DESIGN_WIDTH, trayY); // map / tray
    accents.lineStyle(1, COLORS.accentCyanInt, 0.8);
    accents.lineBetween(0, actionY, DESIGN_WIDTH, actionY); // tray / action
  }

  private drawHexGrid(): void {
    // Subtle hex pattern over the map zone — matches the logo backdrop.
    // Low alpha so it doesn't compete with territories.
    const mapY = LAYOUT.topSafe + LAYOUT.hudHeight;
    const trayY =
      DESIGN_HEIGHT -
      LAYOUT.bottomSafe -
      LAYOUT.navHeight -
      LAYOUT.actionHeight -
      LAYOUT.diceTrayHeight;
    const r = 28; // hex circumradius
    const dx = r * 1.5; // horizontal column step (flat-top hex)
    const dy = r * Math.sqrt(3); // vertical row step
    const cos60 = r / 2;
    const sin60 = (r * Math.sqrt(3)) / 2;

    const g = this.add.graphics();
    g.lineStyle(1, COLORS.hexGridInt, 0.6);

    let col = 0;
    for (let cx = -r; cx <= DESIGN_WIDTH + r; cx += dx, col++) {
      const offsetY = col % 2 === 0 ? 0 : dy / 2;
      for (let cy = mapY + offsetY; cy <= trayY + dy; cy += dy) {
        g.beginPath();
        g.moveTo(cx + r, cy);
        g.lineTo(cx + cos60, cy - sin60);
        g.lineTo(cx - cos60, cy - sin60);
        g.lineTo(cx - r, cy);
        g.lineTo(cx - cos60, cy + sin60);
        g.lineTo(cx + cos60, cy + sin60);
        g.closePath();
        g.strokePath();
      }
    }
  }

  private drawHud(): void {
    // Full-width logo + overlay Back button — same shared chrome the
    // tabbed scenes use. No topInset here so the logo sits flush at
    // y=0, matching every other scene; the iOS notch reservation is
    // handled by the in-canvas LAYOUT bands further down the page.
    //
    // Back tap behavior is store-aware: multiplayer + finished games
    // navigate back immediately (state is safe — MP games can be
    // resumed from Inbox, finished games are read-only). Local
    // single-player games in progress fire a destructive confirm
    // since SP state isn't persisted anywhere — leaving loses it.
    mountPageHeader({
      scene: this,
      logoAssetKey: 'logo',
      showBack: true,
      onBack: () => this.handleBackTap(),
    });

    // Team summary strip — see refreshTeamSummary for the per-team
    // Text construction. Lives in the empty band between HUD bottom
    // (y=250) and the first row of map tiles.
    this.teamSummaryTexts = [];

    // Note: LOG button is created in drawActionPanel() so it lives in
    // the action panel (with the rest of the buttons), not on the map.
    // Turn signaling reuses the existing team chip via refreshStatus —
    // no separate banner overlay on the map.
  }

  /**
   * Back button tap handler. Three cases:
   *   - Multiplayer (any status): navigate immediately — the game
   *     persists server-side and can be resumed from Inbox.
   *   - Finished single-player: navigate immediately — game is
   *     read-only at this point so nothing's lost.
   *   - In-progress single-player: open a destructive ConfirmModal
   *     since SP state isn't persisted anywhere; leaving abandons
   *     the match. Cancel keeps the player in the game.
   */
  private handleBackTap(): void {
    const state = this.store.getState();
    const isMp = this.store.isMultiplayer();
    const finished = state.status.kind === 'victory';
    if (isMp || finished) {
      navigateBack(this);
      return;
    }
    new ConfirmModal(this, {
      title: 'Leave game?',
      body:
        "Your progress in this match will be lost. Single-player games against AI aren't saved yet.",
      confirmLabel: 'Leave',
      cancelLabel: 'Stay',
      destructive: true,
      onConfirm: () => navigateBack(this),
      onCancel: () => {
        // No-op — modal self-destroys on Cancel.
      },
    });
  }

  private openBattleLog(): void {
    if (this.battleLogModal) return;
    this.battleLogModal = new BattleLogModal(this, {
      state: this.store.getState(),
      map: this.store.getMap(),
      designWidth: DESIGN_WIDTH,
      designHeight: DESIGN_HEIGHT,
      onClose: () => {
        this.battleLogModal?.destroy();
        this.battleLogModal = undefined;
      },
    });
  }

  private drawAdjacencies(): void {
    const map = this.store.getMap();
    const territoryById = new Map(map.territories.map((t) => [t.id, t]));
    const drawn = new Set<string>();

    // All four cross-region bridges bend "vertical first" so both ends of
    // each pair are mirror images. The top pair leaves Coral SW/SE
    // heading south, then west/east into the top of the column region.
    // The bottom pair leaves Tidewall S / Deepfin S heading south, then
    // east/west into the Sharkbay row. Default HV would split the top
    // pair (one south-first, one east-first) and break the symmetry.
    //
    // Terra: africa_n|asia_sw — default HV with asia_sw as source would
    // route the horizontal leg LEFT along y=477, bisecting Euro S
    // (324, 477). Forcing VH bends the line right-from-africa_n along
    // the ocean band, then up into asia_sw — clean of every tile.
    const VH_OVERRIDES = new Set<string>([
      'coral_sw|tidewall_n',
      'coral_se|deepfin_n',
      'sharkbay_w|tidewall_s',
      'deepfin_s|sharkbay_e',
      'africa_n|asia_sw',
    ]);

    // Some adjacencies span the full width of a row and would visually
    // bisect every tile in between if drawn as a single L-bend. Route
    // those over the top of the map zone instead — three segments
    // (up, across, down) that run through the empty band between HUD
    // bottom (y=250) and the first tile-top (y≈268).
    //
    // Currently empty — Terra's Bering Strait used this until v0.6
    // tuning dropped it to encourage quicker games.
    const TOP_WRAP_OVERRIDES = new Set<string>();
    const TOP_WRAP_Y = 256;

    const g = this.add.graphics();
    // setY the whole graphics object so every dashed segment drawn
    // below picks up the presentation-layer map offset without each
    // drawDashedSegment call having to thread it through.
    g.setY(MAP_Y_OFFSET);
    g.lineStyle(
      ADJACENCY_LINE_WIDTH,
      COLORS.accentCyanInt,
      0.85
    );

    for (const t of map.territories) {
      for (const neighbor of t.adjacent) {
        const key =
          t.id < neighbor ? `${t.id}|${neighbor}` : `${neighbor}|${t.id}`;
        if (drawn.has(key)) continue;
        drawn.add(key);

        const other = territoryById.get(neighbor);
        if (!other) continue;
        // Within-region squares already touch — drawing a line between
        // their centers would just put dashes through the tile. Only the
        // cross-region "bridges" need explicit lines.
        if (other.region === t.region) continue;

        const a = t.position;
        const b = other.position;

        if (TOP_WRAP_OVERRIDES.has(key)) {
          // Up from a, across the top, down to b. Three segments.
          this.drawDashedSegment(g, a.x, a.y, a.x, TOP_WRAP_Y);
          this.drawDashedSegment(g, a.x, TOP_WRAP_Y, b.x, TOP_WRAP_Y);
          this.drawDashedSegment(g, b.x, TOP_WRAP_Y, b.x, b.y);
          continue;
        }

        // Default rule: bend in the longer dimension first so the
        // perpendicular leg never cuts through a neighbor tile in the
        // source's own region. VH_OVERRIDES forces vertical-first for
        // the two bottom bridges per the user's preference.
        const useHV = VH_OVERRIDES.has(key)
          ? false
          : Math.abs(b.x - a.x) > Math.abs(b.y - a.y);
        const corner = useHV ? { x: b.x, y: a.y } : { x: a.x, y: b.y };
        this.drawDashedSegment(g, a.x, a.y, corner.x, corner.y);
        this.drawDashedSegment(g, corner.x, corner.y, b.x, b.y);
      }
    }
  }

  /**
   * Draws a dashed line from (x1,y1) to (x2,y2). Phaser has no native
   * dash support; we step along the segment painting `DASH_LEN` and
   * skipping `GAP_LEN` until we reach the end.
   */
  private drawDashedSegment(
    g: Phaser.GameObjects.Graphics,
    x1: number,
    y1: number,
    x2: number,
    y2: number
  ): void {
    const DASH_LEN = 9;
    const GAP_LEN = 6;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const total = Math.hypot(dx, dy);
    if (total === 0) return;
    const ux = dx / total;
    const uy = dy / total;
    let traveled = 0;
    while (traveled < total) {
      const segLen = Math.min(DASH_LEN, total - traveled);
      const fromX = x1 + ux * traveled;
      const fromY = y1 + uy * traveled;
      const toX = x1 + ux * (traveled + segLen);
      const toY = y1 + uy * (traveled + segLen);
      g.lineBetween(fromX, fromY, toX, toY);
      traveled += DASH_LEN + GAP_LEN;
    }
  }

  private drawTerritories(): void {
    const map = this.store.getMap();
    const tileSize = map.tileSize;
    for (const t of map.territories) {
      const view = new TerritoryView(
        this,
        t,
        (id) => this.handleTerritoryTap(id),
        tileSize
      );
      this.territoryViews.set(t.id, view);
    }
  }

  private drawActionPanel(): void {
    const actionY =
      DESIGN_HEIGHT - LAYOUT.bottomSafe - LAYOUT.navHeight - LAYOUT.actionHeight;
    const cx = DESIGN_WIDTH / 2;

    // Header row: team chip (left) · phase chip (center) · timer chip
    // (right). All three the same width and height — three equal columns
    // of 220×44 with 15px gaps and 15px outer padding, summing to 720.
    const headerY = actionY + 28;
    const CHIP_W = 220;
    const CHIP_H = 44;
    const teamX = 15 + CHIP_W / 2; // 125
    const phaseX = cx; // 360
    const timerX = DESIGN_WIDTH - 15 - CHIP_W / 2; // 595

    // Team chip — solid fill in the player's color, white tracked text
    // ("AMBER: TURN 1").
    this.teamChipBg = this.add.rectangle(
      teamX,
      headerY,
      CHIP_W,
      CHIP_H,
      COLORS.bgElevatedInt
    );
    this.teamChipText = this.add
      .text(teamX, headerY, '', {
        fontFamily: FONT_HEADER,
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    // Phase chip — outlined box centered horizontally. Border + text
    // recolored per phase.
    this.phaseChipBg = this.add.rectangle(
      phaseX,
      headerY,
      CHIP_W,
      CHIP_H,
      COLORS.bgElevatedInt
    );
    this.phaseChipBg.setStrokeStyle(2, COLORS.accentCyanInt);
    this.phaseChipText = this.add
      .text(phaseX, headerY, '', {
        fontFamily: FONT_HEADER,
        fontSize: '18px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);

    // Timer chip — outlined cyan box, MM:SS in mono. Same column width as
    // the other two chips so the row reads as three equal parts.
    this.timerChipBg = this.add.rectangle(
      timerX,
      headerY,
      CHIP_W,
      CHIP_H,
      COLORS.bgElevatedInt
    );
    this.timerChipBg.setStrokeStyle(2, COLORS.accentCyanInt);
    this.timerText = this.add
      .text(timerX, headerY, '00:00', {
        fontFamily: FONT_MONO,
        fontSize: '28px',
        color: COLORS.accentCyanHex,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    // YOU chip — same slot as the timer chip but only visible in MP.
    // Fill is the local player's seat color so "this browser is the
    // navy one" reads instantly when switching between devices.
    // refreshStatus() fills in the text + color on first run and
    // toggles visibility against the timer chip per mode.
    this.youChipBg = this.add.rectangle(
      timerX,
      headerY,
      CHIP_W,
      CHIP_H,
      COLORS.bgElevatedInt
    );
    this.youChipText = this.add
      .text(timerX, headerY, '', {
        fontFamily: FONT_HEADER,
        fontSize: '18px',
        color: '#ffffff',
        fontStyle: '700',
      })
      .setLetterSpacing(2)
      .setOrigin(0.5);
    this.youChipBg.setVisible(false);
    this.youChipText.setVisible(false);

    // Title — body sans, bold, primary text.
    this.actionTitleText = this.add
      .text(cx, actionY + 75, '', {
        fontFamily: FONT_BODY,
        fontSize: '26px',
        color: COLORS.textPrimary,
        fontStyle: '700',
      })
      .setOrigin(0.5);

    // Detail line — secondary text.
    this.actionDetailText = this.add
      .text(cx, actionY + 103, '', {
        fontFamily: FONT_BODY,
        fontSize: '16px',
        color: COLORS.textSecondary,
      })
      .setOrigin(0.5);

    // Stepper sits in the middle area of the panel.
    this.stepper = new Stepper(this, {
      x: cx - 100,
      y: actionY + 148,
      value: 1,
      min: 1,
      max: 1,
      label: '',
      onChange: () => {},
    });
    this.stepper.setVisible(false);

    const buttonY = actionY + LAYOUT.actionHeight - BUTTON_HEIGHT / 2 - 4;
    this.actionButton = new Button(this, {
      x: cx - 130,
      y: buttonY,
      width: 220,
      text: '',
      style: 'primary',
      onTap: () => {},
    });
    this.endPhaseButton = new Button(this, {
      x: cx + 130,
      y: buttonY,
      width: 220,
      text: '',
      onTap: () => {},
    });

    // Undo button — slim secondary slot just above the main button
    // row. Visible only during deploy phase with deploys to undo.
    this.undoButton = new Button(this, {
      x: cx,
      y: buttonY - BUTTON_HEIGHT - 4,
      width: 200,
      height: 48,
      text: 'Undo Last',
      onTap: () => this.handleUndoLastDeploy(),
    });
    this.undoButton.setVisible(false);

    // LOG button — secondary slot to the right of undo's vertical
    // band, inside the action panel (not on the map). Always visible.
    // Smaller than the primary buttons so the action button row stays
    // visually dominant.
    new Button(this, {
      x: DESIGN_WIDTH - 60,
      y: buttonY - BUTTON_HEIGHT - 4,
      width: 96,
      height: 48,
      text: 'Log',
      onTap: () => this.openBattleLog(),
    });
  }

  private handleUndoLastDeploy(): void {
    const state = this.store.getState();
    const result = this.store.dispatch({
      type: 'undoLastDeploy',
      playerId: state.turn.currentPlayer,
    });
    if (!result.ok) {
      console.warn('Undo rejected:', result.reason);
    }
  }

  private drawNavBar(): void {
    // Shared AppNav strip — same chrome as Home / Inbox / Settings.
    // 'games' is the active slot while we're in a match. Routes:
    // home → Home, games → Inbox, settings → SettingsScene. The
    // chat / leaderboards slots remain console-log placeholders
    // until v1.0+ work builds them.
    //
    // `allowActiveTap` lets re-tapping the highlighted games icon
    // route to Inbox (GameScene is a sub-screen of the games
    // section, not the canonical games list).
    //
    // `onIntercept` gates leaving with a destructive ConfirmModal
    // when a local SP game is in progress — MP and finished games
    // are safe to leave without warning.
    attachBottomNav(this, 'games', {
      allowActiveTap: true,
      onIntercept: (_tappedId, defaultRoute) => {
        const gameOver = this.store.getState().status.kind === 'victory';
        const safeToLeave = this.store.isMultiplayer() || gameOver;
        if (safeToLeave) {
          defaultRoute();
          return;
        }
        new ConfirmModal(this, {
          title: 'Leave game?',
          body:
            'Your progress in this match will be lost. ' +
            'Single-player games against AI aren’t saved yet.',
          confirmLabel: 'Leave game',
          cancelLabel: 'Keep playing',
          destructive: true,
          onConfirm: defaultRoute,
          onCancel: () => {
            /* dismiss — handled by the modal itself */
          },
        });
      },
    });
  }

  private drawDiceTray(): void {
    // Dice tray sits in its own zone between the map and the action
    // panel, vertically centered in the 56px strip.
    const trayY =
      DESIGN_HEIGHT -
      LAYOUT.bottomSafe -
      LAYOUT.navHeight -
      LAYOUT.actionHeight -
      LAYOUT.diceTrayHeight;
    this.diceTray = new DiceTray(
      this,
      DESIGN_WIDTH / 2,
      trayY + LAYOUT.diceTrayHeight / 2
    );
    this.combatVfx = new CombatVfx(this);
    this.eliminationToast = new EliminationToast(this);
    this.autoSkipToast = new AutoSkipToast(this);

    // v0.9.9 Hourglass — if the local player's most recent history
    // entry was an autoSkip, show the toast on mount so they see
    // the consequence of timing out the moment they open the game.
    // Walks newest-first looking for the local player's most recent
    // action; if it was a skip, surface the toast. Only fires once
    // per scene mount (additional mounts re-check from scratch).
    this.maybeShowAutoSkipToastOnMount();
  }

  private maybeShowAutoSkipToastOnMount(): void {
    if (!this.store.isMultiplayer()) return; // SP has no async skips
    const state = this.store.getState();
    const myId = this.store.isLocalTurn() ? state.turn.currentPlayer : null;
    // Walk back to find the local player's most recent action.
    // findLastByActor would be cleaner; inlining for one call site.
    for (let i = state.history.length - 1; i >= 0; i--) {
      const entry = state.history[i]!;
      if (entry.actor == null) continue;
      if (!this.store.isLocalPlayer(entry.actor)) continue;
      if (entry.action.type === 'autoSkip') {
        // Find the matching turnSkipped event to read consecutiveCount.
        const skip = entry.events.find(
          (e) => e.kind === 'turnSkipped'
        );
        const count =
          skip && skip.kind === 'turnSkipped' ? skip.consecutiveCount : 1;
        this.autoSkipToast.show(count);
      }
      break; // first hit is most-recent — don't keep walking
    }
    void myId; // unused but documents intent
  }

  // --- Store subscription / event-queue animation pipeline ---

  /**
   * Push an event batch onto the queue and kick the drain loop if it
   * isn't already running. Each event drains in order with proper
   * pacing — every combat plays its own dice animation, every
   * conquest its own flash — instead of the old "find the first
   * combat and snap everything else" shortcut. This is what makes
   * a server-driven AI turn (which lands as a fat batch via
   * Realtime) watchable instead of just snapping to final state.
   */
  private handleStoreUpdate(events: readonly GameEvent[]): void {
    if (events.length === 0) return;
    this.eventQueue.push(...events);
    if (!this.isDraining) {
      void this.drainEventQueue();
    }
  }

  /**
   * Identify the player whose action this event represents, so the
   * HUD can name them while the event animates. Returns null for
   * events that don't change the focused actor (phase transitions,
   * eliminations, game-over outcomes).
   */
  private actorForEvent(event: GameEvent): PlayerId | null {
    switch (event.kind) {
      case 'deployed':
        return event.playerId;
      case 'combatResolved':
        // Attacker = owner of the FROM tile. viewTerritories tracks
        // the pre-event state at this point in the drain, so this is
        // the right place to read ownership from.
        return this.viewTerritories[event.result.from]?.owner ?? null;
      case 'territoryCaptured':
        // event.to is the new owner — i.e., the attacker.
        return event.to;
      case 'advanced':
      case 'fortified':
        return this.viewTerritories[event.from]?.owner ?? null;
      case 'turnEnded':
        // Hand-off: the NEXT actor is the one whose events will play
        // immediately after this one, so name them now.
        return event.nextPlayer;
      case 'turnSkipped':
        // The skipped player IS the actor of the skip — name them
        // while the (passive) skip event plays.
        return event.playerId;
      case 'deployUndone':
      case 'phaseChanged':
      case 'playerEliminated':
      case 'gameWon':
      case 'turnForfeited':
        return null;
    }
  }

  private async drainEventQueue(): Promise<void> {
    // isDraining gates territory taps (handleTerritoryTap checks it)
    // so the user can't poke the map mid-chain. combatAnimating is
    // intentionally NOT set here — that flag means "dice are mid-roll"
    // and gets toggled inside playCombatEvent so the action panel
    // only paints "Rolling…" during actual combat events, not every
    // deploy/advance/fortify.
    this.isDraining = true;
    // Pre-seed displayedPlayer from the first event's actor so the
    // chip + action panel transition to the correct name BEFORE the
    // first playEvent fires — keeps YOUR TURN from briefly persisting
    // at drain start.
    const firstActor = this.eventQueue[0]
      ? this.actorForEvent(this.eventQueue[0])
      : null;
    if (firstActor !== null) {
      this.displayedPlayer = firstActor;
      this.refreshStatus();
      this.refreshActionPanel();
    }

    while (this.eventQueue.length > 0) {
      const event = this.eventQueue.shift()!;
      // Update displayedPlayer when this event introduces a new actor
      // (e.g., turnEnded handing off to the next AI in a 2v2-ai chain).
      // Refresh status + action panel so the chip and the panel both
      // name them while their events animate.
      const newActor = this.actorForEvent(event);
      if (newActor !== null && newActor !== this.displayedPlayer) {
        this.displayedPlayer = newActor;
        this.refreshStatus();
        this.refreshActionPanel();
      }
      await this.playEvent(event);
    }

    // Final settle — sync viewTerritories to whatever the store now
    // believes (defensive in case any handler missed a delta) and
    // refresh the full screen.
    this.viewTerritories = { ...this.store.getState().territories };
    this.isDraining = false;
    this.displayedPlayer = null;
    this.refreshAll();

    // Open the game-over modal after the drain finishes. The
    // elimination toast already fired inline during the drain (see
    // playEvent's playerEliminated case); the modal opens last so
    // it lands cleanly on a settled board rather than mid-animation.
    // Toast may still be on screen briefly if the game-ending
    // elimination is the local player's — they don't overlap visually
    // (toast at top of map, modal centered) so the brief coexistence
    // is fine.
    if (this.pendingGameWon) {
      this.openGameOverModal();
      this.pendingGameWon = false;
    }
  }

  /**
   * Dispatch a single event to its visual handler. Awaitable so the
   * drain loop pauses for each animation to complete before pulling
   * the next event off the queue.
   */
  private async playEvent(event: GameEvent): Promise<void> {
    switch (event.kind) {
      case 'combatResolved':
        await this.playCombatEvent(event.result);
        return;
      case 'territoryCaptured':
        await this.playCaptureEvent(event.territory, event.to);
        return;
      case 'deployed':
        await this.playDeployEvent(event.territory, event.units);
        return;
      case 'deployUndone':
        await this.playDeployUndoneEvent(event.territory, event.units);
        return;
      case 'advanced':
        await this.playAdvanceEvent(event.from, event.to, event.units);
        return;
      case 'fortified':
        await this.playFortifyEvent(event.from, event.to, event.units);
        return;
      case 'playerEliminated':
        // Fire toast inline — the eliminated player should see the
        // banner the instant their last tile flips, not at end of
        // drain (which could be many AI actions later in a chained
        // batch). Toast sits at top of map; doesn't overlap dice in
        // the action panel below. Modal (if game-ending) still fires
        // at end of drain via pendingGameWon — see drainEventQueue.
        this.showEliminationToast(event.playerId);
        return;
      case 'gameWon':
        this.pendingGameWon = true;
        return;
      case 'turnSkipped':
        // v0.9.9 Hourglass — if the LOCAL player was the one skipped,
        // surface the toast inline. The skip could land via realtime
        // while they're watching the lobby/another tab, OR fire during
        // the AI's chained drain if rotation cycled past the local
        // player while they were elsewhere. Toast renders just below
        // the team chip; doesn't overlap action controls.
        if (this.store.isLocalPlayer(event.playerId)) {
          this.autoSkipToast.show(event.consecutiveCount);
        }
        return;
      case 'turnForfeited':
        // Pure state-transition event — the toast already fired from
        // the preceding turnSkipped (consecutiveCount=3 path). The
        // accompanying playerEliminated event drives the elimination
        // toast separately.
        return;
      case 'phaseChanged':
      case 'turnEnded':
        // Pure state transitions; no per-event visual. HUD and action
        // panel pick these up at the final settle.
        return;
    }
  }

  private playCombatEvent(result: CombatResult): Promise<void> {
    return new Promise<void>((resolve) => {
      // Flip the dice-animating flag now so refreshActionPanel paints
      // "Rolling…" while dice are visibly mid-roll. Reset inside the
      // settle callback so other event handlers in the same drain
      // don't inherit the combat copy.
      this.combatAnimating = true;
      this.refreshActionPanel();
      this.showCombatVfx(result.from, result.to);
      this.diceTray.playRoll(result, () => {
        this.combatAnimating = false;
        this.combatVfx.hide();

        // Apply losses to the playback snapshot. Conquering attacks
        // wipe the defender's units to 0 — ownership flips on the
        // territoryCaptured event that follows.
        const fromT = this.viewTerritories[result.from];
        if (fromT) {
          this.viewTerritories[result.from] = {
            ...fromT,
            units: Math.max(0, fromT.units - result.attackerLosses),
          };
        }
        const toT = this.viewTerritories[result.to];
        if (toT) {
          this.viewTerritories[result.to] = {
            ...toT,
            units: result.conquered
              ? 0
              : Math.max(0, toT.units - result.defenderLosses),
          };
        }
        this.renderTerritoryFromView(result.from);
        this.renderTerritoryFromView(result.to);

        // Loss floaters — skip the defender on a conquest since
        // ownership flips and a "lost N" floater on the new owner's
        // tile would read as the attacker losing units.
        const fromView = this.territoryViews.get(result.from);
        const toView = this.territoryViews.get(result.to);
        if (result.attackerLosses > 0) {
          fromView?.showLossFloater(result.attackerLosses);
        }
        if (!result.conquered && result.defenderLosses > 0) {
          toView?.showLossFloater(result.defenderLosses);
        }

        // Brief gap before the next event so the eye can settle on
        // the result before territoryCaptured/advanced fires.
        this.time.delayedCall(150, () => resolve());
      });
    });
  }

  private playCaptureEvent(
    territory: TerritoryId,
    newOwner: PlayerId
  ): Promise<void> {
    const t = this.viewTerritories[territory];
    if (t) {
      this.viewTerritories[territory] = { ...t, owner: newOwner };
    }
    this.renderTerritoryFromView(territory);

    // Flash the captured tile in the new owner's color so the
    // ownership transition pops visually rather than snapping.
    const player = this.store
      .getState()
      .players.find((p) => p.id === newOwner);
    if (player) {
      const colorInt = Phaser.Display.Color.HexStringToColor(player.color)
        .color;
      this.territoryViews.get(territory)?.flashConquest(colorInt);
    }
    return this.delay(250);
  }

  private playDeployEvent(
    territory: TerritoryId,
    units: number
  ): Promise<void> {
    const t = this.viewTerritories[territory];
    if (t) {
      this.viewTerritories[territory] = { ...t, units: t.units + units };
    }
    this.renderTerritoryFromView(territory);
    // Brief pause so multiple AI deploys in a row are individually
    // legible instead of blurring into one frame.
    return this.delay(180);
  }

  private playDeployUndoneEvent(
    territory: TerritoryId,
    units: number
  ): Promise<void> {
    const t = this.viewTerritories[territory];
    if (t) {
      this.viewTerritories[territory] = {
        ...t,
        units: Math.max(0, t.units - units),
      };
    }
    this.renderTerritoryFromView(territory);
    return this.delay(80);
  }

  private playAdvanceEvent(
    from: TerritoryId,
    to: TerritoryId,
    units: number
  ): Promise<void> {
    const fromT = this.viewTerritories[from];
    const toT = this.viewTerritories[to];
    if (fromT) {
      this.viewTerritories[from] = {
        ...fromT,
        units: Math.max(0, fromT.units - units),
      };
    }
    if (toT) {
      this.viewTerritories[to] = { ...toT, units: toT.units + units };
    }
    this.renderTerritoryFromView(from);
    this.renderTerritoryFromView(to);
    return this.delay(220);
  }

  private playFortifyEvent(
    from: TerritoryId,
    to: TerritoryId,
    units: number
  ): Promise<void> {
    const fromT = this.viewTerritories[from];
    const toT = this.viewTerritories[to];
    if (fromT) {
      this.viewTerritories[from] = {
        ...fromT,
        units: Math.max(0, fromT.units - units),
      };
    }
    if (toT) {
      this.viewTerritories[to] = { ...toT, units: toT.units + units };
    }
    this.renderTerritoryFromView(from);
    this.renderTerritoryFromView(to);
    return this.delay(220);
  }

  /**
   * Render one specific tile using the playback snapshot (not the
   * store's final state). Lets a combat halfway through a batch
   * paint the attacker / defender with the unit counts that existed
   * at THAT step.
   */
  private renderTerritoryFromView(id: TerritoryId): void {
    const view = this.territoryViews.get(id);
    if (!view) return;
    const ts = this.viewTerritories[id];
    if (!ts) return;
    const owner = this.store
      .getState()
      .players.find((p) => p.id === ts.owner);
    if (owner) view.update(ts, owner);
    view.setSelection(this.selectionStateFor(id));
  }

  private delay(ms: number): Promise<void> {
    return new Promise<void>((resolve) => {
      this.time.delayedCall(ms, () => resolve());
    });
  }

  /**
   * Show the elimination toast for `eliminatedId` immediately. Called
   * inline from the playerEliminated event drain so the banner lands
   * the moment the player's last tile flips — no buffering to end of
   * batch. The game-over modal (if this elimination ends the game)
   * opens separately via pendingGameWon at end of drain; the two
   * don't share an onComplete chain since at toast-fire time we don't
   * yet know whether gameWon will follow in the same batch.
   *
   * No-op if the player can't be found in state (defensive).
   */
  private showEliminationToast(eliminatedId: PlayerId): void {
    const state = this.store.getState();
    const eliminated = state.players.find((p) => p.id === eliminatedId);
    if (!eliminated) return;
    // Was `eliminated.kind === 'human'` historically — correct in
    // single-player (only one human = local) but wrong in multi:
    // every connected human saw "YOU HAVE BEEN ELIMINATED" whenever
    // ANY human got eliminated, including on the winner's screen.
    // Gate on isLocalPlayer instead.
    const isLocalPlayer = this.store.isLocalPlayer(eliminated.id);
    this.eliminationToast.show(
      eliminated.name,
      eliminated.color,
      isLocalPlayer
    );
  }

  private openGameOverModal(): void {
    if (this.gameOverModal) return;
    const state = this.store.getState();
    if (state.status.kind !== 'victory') return;

    const playerById = new Map(state.players.map((p) => [p.id, p]));
    const winner = playerById.get(state.status.winner);
    if (!winner) return;

    // Podium order: all survivors share rank 1 (joint winners — every
    // member of the winning team), then last-eliminated → first-eliminated
    // get ranks 2, 3, 4. In FFA the survivor list is always [winner];
    // in a 2v2 team win the survivors are the whole winning team.
    //
    // eliminationOrder is appended in the order players fell, so reading
    // it back-to-front yields next-to-last, second-to-last, etc.
    const podium: PodiumEntry[] = [];
    const survivors = state.players.filter((p) => !p.eliminated);
    // Show the killing-blow seat first among joint winners so the
    // narrative still highlights who finished it.
    const survivorsOrdered = [
      winner,
      ...survivors.filter((p) => p.id !== winner.id),
    ];
    for (const p of survivorsOrdered) {
      podium.push({ rank: 1, player: p, isLocal: this.store.isLocalPlayer(p.id) });
    }
    const reversed = [...state.eliminationOrder].reverse();
    let nextRank = 2;
    for (let i = 0; i < reversed.length && podium.length < 4; i++) {
      const p = playerById.get(reversed[i]!);
      if (!p) continue;
      podium.push({
        rank: nextRank as 1 | 2 | 3 | 4,
        player: p,
        isLocal: this.store.isLocalPlayer(p.id),
      });
      nextRank++;
    }

    // Team win when 2+ survivors share rank 1. Local user is on the
    // winning side iff any survivor is "local" by the store's rules
    // (hot-seat: any human; multiplayer: this browser's seat only).
    // That asymmetry is exactly what stops every browser from seeing
    // "VICTORY / YOU WIN" when another human actually won.
    const teamWin = survivors.length > 1;
    const localWon = survivors.some((p) => this.store.isLocalPlayer(p.id));
    this.gameOverModal = new GameOverModal(this, {
      localWon,
      teamWin,
      winner,
      podium,
      onPlayAgain: () => {
        this.gameOverModal = undefined;
        this.scene.restart();
      },
      onHome: () => {
        this.gameOverModal = undefined;
        this.scene.start('Home');
      },
    });
  }

  // --- Input handlers ---

  private handleTerritoryTap(id: TerritoryId): void {
    // Block taps while dice are rolling OR while an event batch is
    // being drained — the on-screen state isn't yet final, so a tap
    // could land on a tile that's about to change owner/units.
    if (this.combatAnimating || this.isDraining) return;
    const state = this.store.getState();
    if (state.status.kind === 'victory') return;
    // Ignore taps when it isn't the local player's turn. In MP this
    // means a human opponent is up — without this guard, tapping an
    // opponent's tile would still drive local selection + dice UI
    // even though the eventual dispatch would be rejected. In SP /
    // hot-seat, every human seat is "local" so taps flow normally;
    // AI turns return false here and are blocked. Subsumes what used
    // to be a `current?.kind === 'ai'` check.
    if (!this.store.isLocalTurn()) return;
    const ts = state.territories[id];
    if (!ts) return;

    switch (state.turn.phase) {
      case 'deploy':
        this.handleDeployTap(id, ts, state);
        return;
      case 'attack':
        this.handleAttackTap(id, ts, state);
        return;
      case 'fortify':
        this.handleFortifyTap(id, ts, state);
        return;
    }
  }

  private handleDeployTap(
    id: TerritoryId,
    ts: TerritoryState,
    state: GameState
  ): void {
    if (ts.owner !== state.turn.currentPlayer) return;
    this.selectedTerritory = id;
    this.refreshAll();
  }

  private handleAttackTap(
    id: TerritoryId,
    ts: TerritoryState,
    state: GameState
  ): void {
    // Selections are locked while we wait for an advance — only the
    // Advance button can clear it.
    if (state.turn.attack.kind === 'awaitingAdvance') return;

    if (ts.owner === state.turn.currentPlayer) {
      // Tapping a different own territory becomes the new attack source.
      if (ts.units < 2) return;
      if (this.selectedTerritory === id) {
        // Tap-again deselects.
        this.selectedTerritory = null;
        this.targetTerritory = null;
      } else {
        this.selectedTerritory = id;
        this.targetTerritory = null;
        // Default dice = max possible from this territory.
        const max = Math.min(3, ts.units - 1);
        this.attackingDice = (Math.max(1, max) as 1 | 2 | 3);
      }
      this.refreshAll();
      return;
    }

    // Tapping a teammate's territory in team play is a no-op — the
    // attack would be rejected by validateAction's friendly-fire
    // check anyway, but bringing up the dice tray + attack button
    // first would be confusing UX (you'd think the attack was
    // armed). Silent no-op matches the existing pattern for
    // invalid taps (under-2-units own tiles, non-adjacent enemies).
    if (sameTeam(state, ts.owner, state.turn.currentPlayer)) return;

    // Tapping an enemy territory only registers if it's adjacent to the
    // selected source and we have a source.
    if (!this.selectedTerritory) return;
    const fromTerr = this.store
      .getMap()
      .territories.find((t) => t.id === this.selectedTerritory);
    if (!fromTerr || !fromTerr.adjacent.includes(id)) return;
    this.targetTerritory = id;
    this.refreshAll();
  }

  private handleRoll(): void {
    if (!this.selectedTerritory || !this.targetTerritory) return;
    const state = this.store.getState();
    const from = this.selectedTerritory;
    const to = this.targetTerritory;
    const result = this.store.dispatch({
      type: 'attack',
      playerId: state.turn.currentPlayer,
      from,
      to,
      attackingDice: this.attackingDice,
    });
    if (!result.ok) {
      console.warn('Attack rejected:', result.reason);
      return;
    }

    // Multiplayer-only optimistic kick. In SP the store subscriber
    // fires synchronously and playRoll handles cycling itself; here
    // we'd be racing it. In MP the response is ~100-500ms away, and
    // starting the spin pre-emptively hides that latency. Real dice
    // values arrive via the subscriber later and feed into playRoll,
    // which detects the in-flight cycle and just schedules the
    // settle.
    if (this.store.isMultiplayer()) {
      const fromUnits = state.territories[from]?.units ?? 0;
      const toUnits = state.territories[to]?.units ?? 0;
      const attackerCount = Math.min(
        this.attackingDice,
        Math.max(0, fromUnits - 1),
        3
      );
      const defenderCount = Math.min(2, toUnits);
      this.combatAnimating = true;
      this.showCombatVfx(from, to);
      this.diceTray.startCycle(attackerCount, defenderCount);
    }
    // The store subscriber handles the post-roll refresh + settle.
  }

  private handleFortifyTap(
    id: TerritoryId,
    ts: TerritoryState,
    state: GameState
  ): void {
    if (state.turn.fortifyUsed) return;
    // Only own territories matter in fortify.
    if (ts.owner !== state.turn.currentPlayer) return;

    if (!this.selectedTerritory) {
      // Pick the source — must have ≥2 units (need to leave 1 behind).
      if (ts.units < 2) return;
      this.selectedTerritory = id;
      this.fortifyCount = 1;
      this.refreshAll();
      return;
    }

    if (this.selectedTerritory === id) {
      // Tap-again deselects.
      this.selectedTerritory = null;
      this.targetTerritory = null;
      this.refreshAll();
      return;
    }

    // Otherwise we're picking the destination — must be reachable
    // through team territories (own + teammates per v0.7 team rules).
    if (
      !this.isConnectedViaTeam(
        this.selectedTerritory,
        id,
        state.turn.currentPlayer,
        state
      )
    ) {
      // Tapped own territory but not connected — treat as switching source
      // if the new tap has ≥2 units, otherwise ignore.
      if (ts.units >= 2) {
        this.selectedTerritory = id;
        this.targetTerritory = null;
        this.fortifyCount = 1;
        this.refreshAll();
      }
      return;
    }

    this.targetTerritory = id;
    this.refreshAll();
  }

  /**
   * BFS reachability through team-owned tiles. Source must be the
   * acting player's; destination must also be the acting player's
   * (units don't get deposited in a teammate's tile). The PATH can
   * route through teammate space — mirrors the validation rule in
   * src/game/validation.ts so the highlight + tap gating match what
   * the reducer will actually accept.
   */
  private isConnectedViaTeam(
    from: TerritoryId,
    to: TerritoryId,
    player: PlayerId,
    state: GameState
  ): boolean {
    if (from === to) return false;
    const me = state.players.find((p) => p.id === player);
    if (!me) return false;
    const teamByOwner = new Map(state.players.map((p) => [p.id, p.team]));
    const map = this.store.getMap();
    const adj = new Map(map.territories.map((t) => [t.id, t.adjacent]));
    const visited = new Set<TerritoryId>([from]);
    const queue: TerritoryId[] = [from];
    while (queue.length > 0) {
      const here = queue.shift()!;
      const neighbors = adj.get(here) ?? [];
      for (const n of neighbors) {
        if (visited.has(n)) continue;
        const ts = state.territories[n];
        if (!ts) continue;
        if (teamByOwner.get(ts.owner) !== me.team) continue;
        if (n === to) return true;
        visited.add(n);
        queue.push(n);
      }
    }
    return false;
  }

  private handleFortify(): void {
    if (!this.selectedTerritory || !this.targetTerritory) return;
    const state = this.store.getState();
    const from = this.selectedTerritory;
    const to = this.targetTerritory;
    const units = this.fortifyCount;
    const result = this.store.dispatch({
      type: 'fortify',
      playerId: state.turn.currentPlayer,
      from,
      to,
      units,
    });
    if (!result.ok) {
      console.warn('Fortify rejected:', result.reason);
      return;
    }
    // Animated number flying from source to destination — instant
    // visual that the units are on the move.
    this.showFortifyArrow(from, to, units);
    // Reset selection so the panel collapses to "Fortify done".
    this.selectedTerritory = null;
    this.targetTerritory = null;
  }

  private handleAdvance(): void {
    const state = this.store.getState();
    if (state.turn.attack.kind !== 'awaitingAdvance') return;
    const sub = state.turn.attack;
    const advanceUnits = this.advanceCount;
    // Clear the target BEFORE dispatching — the store fires its listeners
    // synchronously, so refreshAll would otherwise paint a stale red ring
    // and a stale Roll button on a territory that's now ours. Source
    // stays selected so the player can attack again from the same tile
    // if it still has ≥2 units (the source-stale check handles the rest).
    this.targetTerritory = null;
    const result = this.store.dispatch({
      type: 'advance',
      playerId: state.turn.currentPlayer,
      from: sub.from,
      to: sub.to,
      units: advanceUnits,
    });
    if (!result.ok) {
      console.warn('Advance rejected:', result.reason);
      return;
    }
    // Local "+N" floater on the conquered tile — instant feedback
    // that the units are moving in.
    this.territoryViews.get(sub.to)?.showGainFloater(advanceUnits);
    // Successful advance — clear the conquest tracker so the NEXT
    // conquest defaults to its own maxUnits.
    this.lastAdvanceTo = undefined;
  }

  private dispatchEndPhase(): void {
    const state = this.store.getState();
    const result = this.store.dispatch({
      type: 'endPhase',
      playerId: state.turn.currentPlayer,
    });
    if (!result.ok) console.warn('End phase rejected:', result.reason);
  }

  private dispatchEndTurn(): void {
    const state = this.store.getState();
    const result = this.store.dispatch({
      type: 'endTurn',
      playerId: state.turn.currentPlayer,
    });
    if (!result.ok) console.warn('End turn rejected:', result.reason);
  }

  private handleDeploy(): void {
    if (!this.selectedTerritory) return;
    const state = this.store.getState();
    const target = this.selectedTerritory;
    const result = this.store.dispatch({
      type: 'deploy',
      playerId: state.turn.currentPlayer,
      territory: target,
      units: 1,
    });
    if (!result.ok) {
      console.warn('Deploy rejected:', result.reason);
      return;
    }
    // Local "+1" floater fires immediately — hides the server
    // round-trip in MP and adds a satisfying beat in SP.
    this.territoryViews.get(target)?.showGainFloater(1);
  }

  /**
   * Brief animated number traveling from source to destination tile.
   * Fires on fortify confirm so the player sees units "move" between
   * tiles before the count updates arrive from the server.
   */
  private showFortifyArrow(
    fromId: TerritoryId,
    toId: TerritoryId,
    units: number
  ): void {
    const map = this.store.getMap();
    const from = map.territories.find((t) => t.id === fromId);
    const to = map.territories.find((t) => t.id === toId);
    if (!from || !to) return;

    const text = this.add
      .text(
        from.position.x,
        from.position.y + MAP_Y_OFFSET,
        `${units}`,
        {
          fontFamily: FONT_MONO,
          fontSize: '32px',
          color: COLORS.accentCyanHex,
          fontStyle: '700',
          stroke: '#000000',
          strokeThickness: 4,
        }
      )
      .setOrigin(0.5)
      .setDepth(170);

    this.tweens.add({
      targets: text,
      x: to.position.x,
      y: to.position.y + MAP_Y_OFFSET,
      alpha: { from: 1, to: 0.2 },
      scale: { from: 1, to: 0.7 },
      duration: 650,
      ease: 'Cubic.easeInOut',
      onComplete: () => text.destroy(),
    });
  }

  // --- Refresh ---

  private refreshAll(): void {
    const state = this.store.getState();

    // Clear selection + restart the turn clock on phase / player change.
    if (
      state.turn.phase !== this.lastPhase ||
      state.turn.currentPlayer !== this.lastPlayer
    ) {
      this.selectedTerritory = null;
      this.targetTerritory = null;
      this.lastPhase = state.turn.phase;
      this.lastPlayer = state.turn.currentPlayer;
      this.resetTurnTimer();
    }

    this.refreshTerritories();
    this.refreshRegionOverlay();
    this.refreshTeamSummary(state);
    this.refreshStatus();
    this.refreshActionPanel();
    this.refreshDiceTray();
  }

  private refreshRegionOverlay(): void {
    this.regionOverlay.refresh(this.store.getState(), this.store.getMap());
  }

  /**
   * Update the team-summary strip. Hidden in pure FFA (every player on
   * a unique team). Visible when ≥2 players share a team — renders
   * each team's segment in its own team-palette color so T1↔cyan,
   * T2↔amber, etc. is consistent with the region rings.
   *
   * Layout: segments laid out horizontally, gap of 24px between them,
   * the whole row centered horizontally at y=stripY.
   */
  private refreshTeamSummary(state: GameState): void {
    // Tear down previous frame's text objects — team membership is
    // immutable at game start, but FFA-vs-team-mode determination
    // happens fresh on each refresh and a player elimination might
    // change the visible composition later.
    for (const t of this.teamSummaryTexts) t.destroy();
    this.teamSummaryTexts = [];

    const byTeam = new Map<number, GameState['players'][number][]>();
    for (const p of state.players) {
      const list = byTeam.get(p.team) ?? [];
      list.push(p);
      byTeam.set(p.team, list);
    }
    // FFA short-circuit — every player on a unique team means there's
    // no useful team info to surface.
    if (byTeam.size === state.players.length) return;

    const stripY = LAYOUT.topSafe + LAYOUT.hudHeight + 14; // y=264
    const SEG_GAP = 24;
    const sortedTeams = [...byTeam.entries()].sort(([a], [b]) => a - b);

    // Build segment label strings; measure their widths to lay them
    // out as a centered row.
    const segments = sortedTeams.map(([team, members]) => {
      const names = members.map((m) => m.name.toUpperCase()).join('·');
      return { team, label: `T${team}: ${names}` };
    });

    // Create temp texts to measure widths, then position them.
    const tempTexts = segments.map((s) =>
      this.add
        .text(0, 0, s.label, {
          fontFamily: FONT_MONO,
          fontSize: '13px',
          color: teamColorHex(s.team),
          fontStyle: '700',
        })
        .setLetterSpacing(2)
        .setOrigin(0, 0.5)
    );
    const totalWidth =
      tempTexts.reduce((sum, t) => sum + t.width, 0) +
      SEG_GAP * (tempTexts.length - 1);
    let cursorX = DESIGN_WIDTH / 2 - totalWidth / 2;
    for (const t of tempTexts) {
      t.setPosition(cursorX, stripY);
      cursorX += t.width + SEG_GAP;
    }
    this.teamSummaryTexts = tempTexts;
  }


  /**
   * Resolve the attacker / defender tile centers from the map and hand
   * them to the combat VFX. No-op if either id can't be found (defensive
   * — the map and the action's territory ids should always agree).
   */
  private showCombatVfx(from: TerritoryId, to: TerritoryId): void {
    const map = this.store.getMap();
    const fromTerr = map.territories.find((t) => t.id === from);
    const toTerr = map.territories.find((t) => t.id === to);
    if (!fromTerr || !toTerr) return;
    // Shift positions through the presentation-layer map offset so
    // the reticle + arc line up with the rendered territory squares.
    this.combatVfx.show(
      { x: fromTerr.position.x, y: fromTerr.position.y + MAP_Y_OFFSET },
      { x: toTerr.position.x, y: toTerr.position.y + MAP_Y_OFFSET }
    );
  }

  private refreshDiceTray(): void {
    // playRoll has the tray during the animation; bail.
    if (this.combatAnimating) return;

    const state = this.store.getState();

    // Tray is grayed unless we're mid-attack with both source and target
    // chosen and not waiting on an advance.
    if (
      state.turn.phase !== 'attack' ||
      state.turn.attack.kind === 'awaitingAdvance' ||
      !this.selectedTerritory ||
      !this.targetTerritory
    ) {
      this.diceTray.showIdle();
      return;
    }

    const fromUnits =
      state.territories[this.selectedTerritory]?.units ?? 0;
    const toUnits = state.territories[this.targetTerritory]?.units ?? 0;
    const attackerCount = Math.min(
      this.attackingDice,
      Math.max(0, fromUnits - 1),
      3
    );
    const defenderCount = Math.min(2, toUnits);
    this.diceTray.preview(attackerCount, defenderCount);
  }

  private refreshTerritories(): void {
    const state = this.store.getState();
    const playerById = new Map(state.players.map((p) => [p.id, p]));
    for (const t of this.store.getMap().territories) {
      const view = this.territoryViews.get(t.id);
      const ts = state.territories[t.id];
      const owner = ts ? playerById.get(ts.owner) : undefined;
      if (view && ts && owner) view.update(ts, owner);
      view?.setSelection(this.selectionStateFor(t.id));
    }
  }

  private selectionStateFor(
    id: TerritoryId
  ): 'none' | 'selected' | 'target' | 'destination' {
    if (id === this.selectedTerritory) return 'selected';
    if (id === this.targetTerritory) {
      // In fortify the destination is friendly — render it cyan, not red.
      return this.store.getState().turn.phase === 'fortify'
        ? 'destination'
        : 'target';
    }
    return 'none';
  }

  private refreshStatus(): void {
    const state = this.store.getState();
    const playerById = new Map(state.players.map((p) => [p.id, p]));

    // Team chip doubles as the turn indicator. Style depends on mode:
    //   Single-player        → team color + "NAME: TURN N" (classic)
    //   MP, your turn        → bright cyan, big "YOUR TURN" with pulse
    //   MP, opponent's turn  → muted dark, small "NAME'S TURN" gray
    //   Victory              → winner's color + "NAME: TURN N"
    //
    // displayedPlayer overrides the chip's player ID during a drain
    // so a 2v2-ai chain (which by the time we render is back to my
    // turn in the store) still names the AI whose events are
    // animating right now.
    const chipPlayerId =
      this.displayedPlayer ??
      (state.status.kind === 'victory'
        ? state.status.winner
        : state.turn.currentPlayer);
    const focusPlayer = playerById.get(chipPlayerId);
    const inMultiplayer = this.store.isMultiplayer();
    const showingMe = this.store.isLocalPlayer(chipPlayerId);
    const liveGame = state.status.kind === 'inProgress';

    // Always stop any prior pulse before re-styling so the chip
    // doesn't carry mid-tween alpha into a new state.
    this.teamChipPulse?.stop();
    this.teamChipPulse = null;
    this.teamChipBg.setAlpha(1);

    if (inMultiplayer && liveGame && showingMe) {
      // YOUR TURN — Solar (warm orange) from the team palette so it
      // pops against the deep-navy canvas and the cool cyan accents
      // used elsewhere. Pulsing alpha + large bold text make it the
      // single most attention-grabbing thing in the panel.
      this.teamChipBg.setFillStyle(TEAM_COLORS_INT[1]!); // Solar
      this.teamChipBg.setStrokeStyle(0);
      this.teamChipText
        .setText('YOUR TURN')
        .setColor('#0a0e14')
        .setFontSize(22);
      this.teamChipPulse = this.tweens.add({
        targets: this.teamChipBg,
        alpha: { from: 1, to: 0.55 },
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (inMultiplayer && liveGame && focusPlayer) {
      // Opponent's turn — muted dark fill, small gray text, no pulse.
      this.teamChipBg.setFillStyle(COLORS.bgElevatedInt);
      this.teamChipBg.setStrokeStyle(0);
      this.teamChipText
        .setText(`${focusPlayer.name.toUpperCase()}'S TURN`)
        .setColor(COLORS.textMuted)
        .setFontSize(16);
    } else if (focusPlayer) {
      // Single-player / victory — classic team chip.
      const fill = Phaser.Display.Color.HexStringToColor(focusPlayer.color)
        .color;
      this.teamChipBg.setFillStyle(fill);
      this.teamChipBg.setStrokeStyle(0);
      this.teamChipText
        .setText(`${focusPlayer.name.toUpperCase()}: TURN ${state.turnNumber}`)
        .setColor('#ffffff')
        .setFontSize(18);
    } else {
      this.teamChipBg.setFillStyle(COLORS.bgElevatedInt);
      this.teamChipText.setText('');
    }

    // Phase badge: label + color depend on phase / sub-phase / status.
    const phase = phaseDescriptor(state);
    this.phaseChipText.setText(phase.label);
    this.phaseChipText.setColor(phase.hex);
    this.phaseChipBg.setStrokeStyle(2, phase.int);

    // Right slot of the chip row: the timer chip is now always
    // visible (SP shows the 60-sec live MM:SS countdown; MP shows
    // the 24h turn deadline from store.getTurnExpiresAt() per
    // v0.9.9 Hourglass). YOU·NAME chip retired — team chip's YOUR
    // TURN / OPPONENT'S TURN treatment already communicates
    // control identity. The youChipBg/Text fields are kept (hidden)
    // for diff minimality; cleanup will remove them later.
    this.youChipBg.setVisible(false);
    this.youChipText.setVisible(false);
    this.timerChipBg.setVisible(true);
    this.timerText.setVisible(true);
    // For MP, set an initial render of the timer text — update() will
    // tick it every frame and gate on integer-minute / second changes
    // for repaint. SP path is the existing MM:SS countdown that the
    // existing update() handler drives unchanged.
    if (inMultiplayer) {
      this.refreshMpTimerChip();
    }
  }

  /**
   * v0.9.9 Hourglass — render the 24-hour turn countdown in slot 3.
   * Reads store.getTurnExpiresAt() and formats per granularity rules:
   *   - > 1h remaining: "23H LEFT" / muted "OPPONENT 18H"
   *   - 1m-1h remaining: "47M LEFT" / muted "OPPONENT 47M"
   *   - < 1m remaining: "47S LEFT" with red tint
   *   - 0 / past: "EXPIRED" in red
   *
   * Called from update() every frame; gates internal redraws on
   * integer-second / integer-minute boundaries so we don't repaint
   * the text 60x per second.
   */
  private refreshMpTimerChip(): void {
    const expiresIso = this.store.getTurnExpiresAt();
    if (!expiresIso) {
      // No timer (AI's turn, finished game, etc.) — empty chip.
      this.timerText.setText('—');
      this.timerText.setColor(COLORS.textMuted);
      this.timerChipBg.setStrokeStyle(2, COLORS.borderSubtleInt);
      return;
    }
    const remainingMs = new Date(expiresIso).getTime() - Date.now();
    const isLocalTurn = this.store.isLocalTurn();
    const { label, color, strokeColor } = formatTurnTimer(
      remainingMs,
      isLocalTurn
    );
    this.timerText.setText(label);
    this.timerText.setColor(color);
    this.timerChipBg.setStrokeStyle(2, strokeColor);
  }

  private refreshActionPanel(): void {
    const state = this.store.getState();
    // Default to hidden — refreshDeployPanel re-enables it when
    // there's a deploy on this turn's stack to reverse.
    this.undoButton.setVisible(false);

    if (state.status.kind === 'victory') {
      // Game-over modal owns the result UI; keep the panel quiet behind
      // it so nothing shows through if the modal is mid-fade or has
      // been dismissed (the player can still see the final board).
      this.actionTitleText.setText('');
      this.actionDetailText.setText('');
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(false);
      return;
    }

    if (this.combatAnimating) {
      this.actionTitleText.setText('Rolling…');
      this.actionDetailText.setText('');
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(false);
      return;
    }

    const current = state.players.find(
      (p) => p.id === state.turn.currentPlayer
    );
    if (current && current.kind === 'ai') {
      this.actionTitleText.setText('AI thinking…');
      this.actionDetailText.setText(
        `${current.name.toUpperCase()} is making its move`
      );
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(false);
      return;
    }

    // Multiplayer: if the UI is showing a non-local player (either
    // another human's turn, or a drain animating someone else's
    // actions), hide the phase buttons entirely. displayedPlayer
    // gives us the actor whose events are currently animating; if
    // unset, fall back to state.turn.currentPlayer. Either way, if
    // that player isn't local, kill the controls.
    //
    // Single-player hot-seat: isLocalPlayer returns true for any
    // human seat, so this branch only fires in MP for non-local
    // humans / AI seats.
    const focusedActor =
      this.displayedPlayer ?? state.turn.currentPlayer;
    const focusedPlayer = state.players.find((p) => p.id === focusedActor);
    if (
      this.store.isMultiplayer() &&
      focusedPlayer &&
      !this.store.isLocalPlayer(focusedActor)
    ) {
      this.actionTitleText.setText(`${focusedPlayer.name.toUpperCase()}'S TURN`);
      this.actionDetailText.setText(
        focusedPlayer.kind === 'ai'
          ? 'AI is making its move'
          : 'Waiting for them to play…'
      );
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(false);
      return;
    }

    switch (state.turn.phase) {
      case 'deploy':
        this.refreshDeployPanel(state.turn.reinforcementsRemaining);
        return;
      case 'attack':
        this.refreshAttackPanel(state.turn.attack);
        return;
      case 'fortify':
        this.refreshFortifyPanel();
        return;
    }
  }

  private refreshDeployPanel(remaining: number): void {
    const state = this.store.getState();
    const isSetup = state.turnNumber === 0;
    const titlePrefix = isSetup ? 'Setup · ' : '';
    // Show the Undo button as long as there's something to undo — works
    // both while reinforcements are still in hand AND once the player
    // has placed everything but hasn't ended the phase yet.
    this.undoButton.setVisible(state.turn.deploysThisTurn.length > 0);
    // If the active player controls one or more full regions, surface
    // the bonus inline so they can see WHY their reinforcement count is
    // higher than the base formula. During setup the count is 1
    // regardless of region control, so we suppress the bonus tag then.
    const bonus = isSetup
      ? 0
      : computeRegionBonus(state, this.store.getMap(), state.turn.currentPlayer);
    const bonusTag = bonus > 0 ? ` (+${bonus} region bonus)` : '';
    this.actionTitleText.setText(
      `${titlePrefix}Reinforcements: ${remaining}${bonusTag}`
    );
    this.stepper.setVisible(false);

    if (remaining > 0) {
      this.endPhaseButton.setVisible(false);
      this.actionButton.setVisible(true);
      this.actionButton.setText('Deploy 1');
      this.actionButton.setOnTap(() => this.handleDeploy());

      if (this.selectedTerritory) {
        const ts = this.store.getState().territories[this.selectedTerritory];
        const t = this.store
          .getMap()
          .territories.find((x) => x.id === this.selectedTerritory);
        this.actionDetailText.setText(
          `${t?.name ?? '?'} · ${ts?.units ?? 0} units`
        );
        this.actionButton.setEnabled(true);
      } else {
        this.actionDetailText.setText(
          isSetup
            ? 'Setup round — every seat deploys before any attacks'
            : 'Tap one of your territories to select'
        );
        this.actionButton.setEnabled(false);
      }
    } else {
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(true);
      this.endPhaseButton.setText(isSetup ? 'End Setup Deploy' : 'End Deploy');
      this.endPhaseButton.setOnTap(() => this.dispatchEndPhase());
      this.endPhaseButton.setEnabled(true);
      this.actionDetailText.setText(
        isSetup ? 'Pass to next seat' : 'All reinforcements placed'
      );
    }
  }

  private refreshAttackPanel(attack: AttackSubPhase): void {
    if (attack.kind === 'awaitingAdvance') {
      this.refreshAdvancePanel(attack);
      return;
    }

    const state = this.store.getState();
    const fromTerr = this.selectedTerritory
      ? this.store
          .getMap()
          .territories.find((t) => t.id === this.selectedTerritory)
      : null;
    const toTerr = this.targetTerritory
      ? this.store
          .getMap()
          .territories.find((t) => t.id === this.targetTerritory)
      : null;

    // Drop a stale source if it's no longer attackable (e.g. after losses).
    if (this.selectedTerritory) {
      const fromUnits =
        state.territories[this.selectedTerritory]?.units ?? 0;
      if (fromUnits < 2) {
        this.selectedTerritory = null;
        this.targetTerritory = null;
      }
    }

    // Drop a stale target if it's now owned by the current player — they
    // just conquered + advanced into it, so it's no longer a valid attack
    // target. Belt-and-suspenders alongside the explicit clear in
    // handleAdvance, in case a future code path leaves a stale target.
    if (this.targetTerritory) {
      const t = state.territories[this.targetTerritory];
      if (t && t.owner === state.turn.currentPlayer) {
        this.targetTerritory = null;
      }
    }

    if (!this.selectedTerritory) {
      this.actionTitleText.setText('Attack phase');
      this.actionDetailText.setText(
        'Tap a territory you own (2+ units) to attack from'
      );
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(true);
      this.endPhaseButton.setText('End Attack');
      this.endPhaseButton.setOnTap(() => this.dispatchEndPhase());
      this.endPhaseButton.setEnabled(true);
      return;
    }

    if (!this.targetTerritory) {
      this.actionTitleText.setText(`From: ${fromTerr?.name ?? '?'}`);
      this.actionDetailText.setText('Tap an adjacent enemy territory');
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(true);
      this.endPhaseButton.setText('End Attack');
      this.endPhaseButton.setOnTap(() => this.dispatchEndPhase());
      this.endPhaseButton.setEnabled(true);
      return;
    }

    // Both selected — show dice picker + Roll.
    const fromUnits = state.territories[this.selectedTerritory]?.units ?? 0;
    const maxDice = Math.min(3, Math.max(1, fromUnits - 1));
    if (this.attackingDice > maxDice) this.attackingDice = maxDice as 1 | 2 | 3;

    this.actionTitleText.setText(
      `${fromTerr?.name ?? '?'} → ${toTerr?.name ?? '?'}`
    );
    this.actionDetailText.setText('');

    this.stepper.setLabel('Dice');
    this.stepper.setRange(1, maxDice);
    this.stepper.setValue(this.attackingDice);
    this.stepper.setOnChange((v) => {
      this.attackingDice = v as 1 | 2 | 3;
      // Re-preview the tray so the visible die count tracks the stepper.
      this.refreshDiceTray();
    });
    this.stepper.setVisible(true);

    this.actionButton.setVisible(true);
    this.actionButton.setText('Roll');
    this.actionButton.setOnTap(() => this.handleRoll());
    this.actionButton.setEnabled(true);

    this.endPhaseButton.setVisible(true);
    this.endPhaseButton.setText('End Attack');
    this.endPhaseButton.setOnTap(() => this.dispatchEndPhase());
    this.endPhaseButton.setEnabled(true);
  }

  private refreshAdvancePanel(
    sub: Extract<AttackSubPhase, { kind: 'awaitingAdvance' }>
  ): void {
    const map = this.store.getMap();
    const fromTerr = map.territories.find((t) => t.id === sub.from);
    const toTerr = map.territories.find((t) => t.id === sub.to);

    this.actionTitleText.setText(`Conquered ${toTerr?.name ?? '?'}!`);
    this.actionDetailText.setText(`Move units from ${fromTerr?.name ?? '?'}`);

    // Default the stepper to maxUnits on each fresh conquest — most
    // of the time the player wants to push everything forward. They
    // can step it down to minUnits if they want to leave a garrison.
    // Preserve their manual adjustment as long as we're still on
    // the same conquest (refreshAdvancePanel is called repeatedly
    // during the awaitingAdvance state for HUD updates).
    if (this.lastAdvanceTo !== sub.to) {
      this.advanceCount = sub.maxUnits;
      this.lastAdvanceTo = sub.to;
    } else if (
      this.advanceCount < sub.minUnits ||
      this.advanceCount > sub.maxUnits
    ) {
      this.advanceCount = sub.maxUnits;
    }
    this.stepper.setLabel('Advance');
    this.stepper.setRange(sub.minUnits, sub.maxUnits);
    this.stepper.setValue(this.advanceCount);
    this.stepper.setOnChange((v) => {
      this.advanceCount = v;
      this.actionButton.setText(`Advance ${v}`);
    });
    this.stepper.setVisible(true);

    this.actionButton.setVisible(true);
    this.actionButton.setText(`Advance ${this.advanceCount}`);
    this.actionButton.setOnTap(() => this.handleAdvance());
    this.actionButton.setEnabled(true);

    // No End Attack — must resolve advance first.
    this.endPhaseButton.setVisible(false);
  }

  private refreshFortifyPanel(): void {
    const state = this.store.getState();
    const map = this.store.getMap();

    // Already used the one fortify this turn — nothing left but End Turn.
    if (state.turn.fortifyUsed) {
      this.actionTitleText.setText('Fortify complete');
      this.actionDetailText.setText('Ready to end turn');
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(true);
      this.endPhaseButton.setText('End Turn');
      this.endPhaseButton.setOnTap(() => this.dispatchEndTurn());
      this.endPhaseButton.setEnabled(true);
      return;
    }

    // Drop a stale source if it's no longer fortify-eligible.
    if (this.selectedTerritory) {
      const fromUnits =
        state.territories[this.selectedTerritory]?.units ?? 0;
      if (fromUnits < 2) {
        this.selectedTerritory = null;
        this.targetTerritory = null;
      }
    }

    if (!this.selectedTerritory) {
      this.actionTitleText.setText('Fortify phase');
      this.actionDetailText.setText(
        'Tap a territory you own (2+ units) to move from'
      );
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(true);
      this.endPhaseButton.setText('End Turn');
      this.endPhaseButton.setOnTap(() => this.dispatchEndTurn());
      this.endPhaseButton.setEnabled(true);
      return;
    }

    const fromTerr = map.territories.find(
      (t) => t.id === this.selectedTerritory
    );

    if (!this.targetTerritory) {
      this.actionTitleText.setText(`From: ${fromTerr?.name ?? '?'}`);
      this.actionDetailText.setText(
        "Tap a territory connected through your team's"
      );
      this.stepper.setVisible(false);
      this.actionButton.setVisible(false);
      this.endPhaseButton.setVisible(true);
      this.endPhaseButton.setText('End Turn');
      this.endPhaseButton.setOnTap(() => this.dispatchEndTurn());
      this.endPhaseButton.setEnabled(true);
      return;
    }

    // Both selected — show stepper + Confirm + End Turn.
    const toTerr = map.territories.find(
      (t) => t.id === this.targetTerritory
    );
    const fromUnits = state.territories[this.selectedTerritory]?.units ?? 0;
    const maxMove = Math.max(1, fromUnits - 1);
    if (this.fortifyCount > maxMove) this.fortifyCount = maxMove;
    if (this.fortifyCount < 1) this.fortifyCount = 1;

    this.actionTitleText.setText(
      `${fromTerr?.name ?? '?'} → ${toTerr?.name ?? '?'}`
    );
    this.actionDetailText.setText('');

    this.stepper.setLabel('Move');
    this.stepper.setRange(1, maxMove);
    this.stepper.setValue(this.fortifyCount);
    this.stepper.setOnChange((v) => {
      this.fortifyCount = v;
      this.actionButton.setText(`Move ${v}`);
    });
    this.stepper.setVisible(true);

    this.actionButton.setVisible(true);
    this.actionButton.setText(`Move ${this.fortifyCount}`);
    this.actionButton.setOnTap(() => this.handleFortify());
    this.actionButton.setEnabled(true);

    this.endPhaseButton.setVisible(true);
    this.endPhaseButton.setText('End Turn');
    this.endPhaseButton.setOnTap(() => this.dispatchEndTurn());
    this.endPhaseButton.setEnabled(true);
  }
}

interface PhaseDescriptor {
  readonly label: string;
  readonly int: number;
  readonly hex: string;
}

/**
 * Picks the label and accent color for the phase badge. Setup gets its
 * own label so the player understands turn 0 is special; awaitingAdvance
 * surfaces ADVANCE so the player notices they need to resolve it before
 * doing anything else; victory takes over the chip entirely.
 */
function phaseDescriptor(state: GameState): PhaseDescriptor {
  if (state.status.kind === 'victory') {
    return {
      label: 'GAME OVER',
      int: COLORS.accentGreenInt,
      hex: COLORS.accentGreenHex,
    };
  }
  if (state.turnNumber === 0) {
    return {
      label: 'SETUP PHASE',
      int: COLORS.accentCyanInt,
      hex: COLORS.accentCyanHex,
    };
  }
  switch (state.turn.phase) {
    case 'deploy':
      return {
        label: 'DEPLOY PHASE',
        int: COLORS.accentCyanInt,
        hex: COLORS.accentCyanHex,
      };
    case 'attack':
      if (state.turn.attack.kind === 'awaitingAdvance') {
        return {
          label: 'ADVANCE PHASE',
          int: COLORS.accentAmberInt,
          hex: COLORS.accentAmberHex,
        };
      }
      return {
        label: 'ATTACK PHASE',
        int: COLORS.accentRedInt,
        hex: COLORS.accentRedHex,
      };
    case 'fortify':
      return {
        label: 'FORTIFY PHASE',
        int: COLORS.accentGreenInt,
        hex: COLORS.accentGreenHex,
      };
  }
}
